/**
 * ServicesSection — MADE-257
 *
 * Two service CATEGORY cards — not individual products.
 * Explicit decision: Custom 3D Prints + Commercial/Production.
 * No Medusa API calls. No price tags.
 * "View Shop →" link below cards.
 */

import { Box, Container, Typography, Grid, Card, CardContent } from '@mui/material';

/** B&W layer-stack SVG — custom 3D printing service */
function Custom3DPrintsSvg() {
  const layers = [0, 1, 2, 3, 4];
  return (
    <Box
      role="img"
      aria-label="Custom 3D prints — precision layer-by-layer manufacturing"
      sx={{
        width: '100%',
        height: 220,
        background: 'linear-gradient(135deg, #111 0%, #1e1e1e 100%)',
        borderRadius: '12px 12px 0 0',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        width="84%"
        height="84%"
        viewBox="0 0 300 220"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Isometric layered stack */}
        {layers.map((i) => {
          const y = 170 - i * 22;
          const w = 130 - i * 10;
          const cx = 150;
          const alpha = 0.28 + i * 0.12;
          return (
            <g key={i}>
              {/* Top diamond face */}
              <polygon
                points={`${cx},${y - 13} ${cx + w / 2},${y} ${cx},${y + 13} ${cx - w / 2},${y}`}
                fill={`rgba(255,255,255,${0.02 + i * 0.03})`}
                stroke={`rgba(255,255,255,${alpha})`}
                strokeWidth="1.2"
              />
              {/* Right face */}
              <polygon
                points={`${cx + w / 2},${y} ${cx + w / 2},${y + 18} ${cx},${y + 31} ${cx},${y + 13}`}
                fill={`rgba(255,255,255,${0.01 + i * 0.015})`}
                stroke={`rgba(255,255,255,${alpha * 0.65})`}
                strokeWidth="1"
              />
            </g>
          );
        })}

        {/* Nozzle head */}
        <rect x="136" y="18" width="28" height="36" rx="4" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" />
        <rect x="140" y="12" width="20" height="8" rx="2" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />

        {/* Nozzle tip */}
        <polygon points="136,54 164,54 159,70 141,70" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" />

        {/* Purple filament beam */}
        <line x1="150" y1="70" x2="150" y2="84" stroke="rgba(102,126,234,0.9)" strokeWidth="2.5" strokeDasharray="3 2" />

        {/* Rail guides */}
        <line x1="40" y1="18" x2="40" y2="188" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
        <line x1="260" y1="18" x2="260" y2="188" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
        <line x1="40" y1="188" x2="260" y2="188" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />

        {/* Corner marks */}
        <path d="M 16 8 L 16 22 M 16 8 L 30 8" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
        <path d="M 284 8 L 284 22 M 284 8 L 270 8" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
      </svg>
    </Box>
  );
}

/** B&W grid-of-components SVG — commercial/production service */
function CommercialProductionSvg() {
  const columns = [0, 1, 2, 3];
  const rows = [0, 1, 2];
  return (
    <Box
      role="img"
      aria-label="Commercial production — industrial-scale manufacturing output"
      sx={{
        width: '100%',
        height: 220,
        background: 'linear-gradient(135deg, #0a0a14 0%, #161625 100%)',
        borderRadius: '12px 12px 0 0',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        width="86%"
        height="86%"
        viewBox="0 0 300 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Production grid — repeated precision components */}
        {columns.map((col) =>
          rows.map((row) => {
            const cx = 38 + col * 74;
            const cy = 38 + row * 58;
            return (
              <g key={`${col}-${row}`}>
                <circle cx={cx} cy={cy} r={20} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" />
                <circle cx={cx} cy={cy} r={12} fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
                <circle cx={cx} cy={cy} r={4.5} fill="rgba(255,255,255,0.75)" />
                {/* Spoke marks at 0/90/180/270 */}
                <line x1={cx} y1={cy - 6} x2={cx} y2={cy - 16} stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
                <line x1={cx} y1={cy + 6} x2={cx} y2={cy + 16} stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
                <line x1={cx - 6} y1={cy} x2={cx - 16} y2={cy} stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
                <line x1={cx + 6} y1={cy} x2={cx + 16} y2={cy} stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
              </g>
            );
          })
        )}

        {/* Production flow line */}
        <line x1="10" y1="188" x2="284" y2="188" stroke="rgba(255,255,255,0.14)" strokeWidth="1" strokeDasharray="4 3" />
        <polygon points="282,184 294,188 282,192" fill="rgba(255,255,255,0.22)" />

        {/* Batch count label area */}
        <rect x="248" y="8" width="44" height="18" rx="3" fill="none" stroke="rgba(102,126,234,0.4)" strokeWidth="1" />
        <line x1="254" y1="14" x2="286" y2="14" stroke="rgba(102,126,234,0.25)" strokeWidth="1" />
        <line x1="254" y1="19" x2="278" y2="19" stroke="rgba(102,126,234,0.25)" strokeWidth="1" />

        {/* Corner marks */}
        <path d="M 8 8 L 8 22 M 8 8 L 22 8" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
        <path d="M 292 8 L 292 22 M 292 8 L 278 8" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
      </svg>
    </Box>
  );
}

export function ServicesSection() {
  return (
    <Box
      component="section"
      aria-labelledby="services-headline"
      sx={{ backgroundColor: '#f8f9fb', py: { xs: 8, md: 12 } }}
    >
      <Container maxWidth="lg">
        <Typography
          id="services-headline"
          variant="h2"
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '2rem', md: '2.75rem' },
            color: '#1a1a2e',
            textAlign: 'center',
            mb: 2,
            letterSpacing: '-0.01em',
          }}
        >
          What We Make
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: '#666',
            textAlign: 'center',
            mb: 7,
            maxWidth: 560,
            mx: 'auto',
            fontSize: '1.05rem',
            lineHeight: 1.75,
          }}
        >
          Not a product catalogue — a manufacturing capability. Tell us what you need.
        </Typography>

        <Grid container spacing={4}>
          {/* Card 1 — Custom 3D Prints */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid rgba(0,0,0,0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                '&:hover': {
                  boxShadow: '0 8px 36px rgba(102,126,234,0.14)',
                  transform: 'translateY(-3px)',
                },
              }}
            >
              <Custom3DPrintsSvg />
              <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  id="service-custom-3d"
                  variant="h3"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.35rem', md: '1.5rem' },
                    color: '#1a1a2e',
                    mb: 2,
                  }}
                >
                  Custom 3D Prints
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: '#555', lineHeight: 1.8, mb: 3, flex: 1 }}
                >
                  Have a design in mind? We manufacture bespoke components to your exact
                  specifications — from single prototypes to short-run production batches.
                  Marine-grade materials and tight tolerances as standard.
                </Typography>
                <Box
                  component="a"
                  href="/shop/contact"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: '#667eea',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Get a Custom Quote →
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 2 — Commercial/Production */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid rgba(0,0,0,0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                '&:hover': {
                  boxShadow: '0 8px 36px rgba(102,126,234,0.14)',
                  transform: 'translateY(-3px)',
                },
              }}
            >
              <CommercialProductionSvg />
              <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  id="service-commercial"
                  variant="h3"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.35rem', md: '1.5rem' },
                    color: '#1a1a2e',
                    mb: 2,
                  }}
                >
                  Commercial / Production
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: '#555', lineHeight: 1.8, mb: 3, flex: 1 }}
                >
                  Scale your output with a manufacturing partner who has been delivering in
                  Barbados since 1992. We support high-volume runs in engineering-grade polymers
                  for marine, industrial, and commercial customers.
                </Typography>
                <Box
                  component="a"
                  href="/shop/custom-print"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: '#667eea',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Get a Production Quote →
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* View Shop CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Box
            component="a"
            href="/shop"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              color: '#667eea',
              fontWeight: 600,
              fontSize: '1.05rem',
              textDecoration: 'none',
              borderBottom: '2px solid rgba(102,126,234,0.3)',
              pb: 0.25,
              transition: 'border-color 0.2s',
              '&:hover': { borderBottomColor: '#667eea' },
            }}
          >
            View Shop →
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
